import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Injector,
  Input,
  OnInit,
  Provider,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { WithSubscription } from '@shared/base';
import { isAfter, isBefore, isSame } from 'ngx-bootstrap/chronos';

export const TIMEPICKER_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  /* tslint:disable-next-line: no-use-before-declare */
  useExisting: forwardRef(() => TimepickerComponent),
  multi: true
};

const TIMEPICKER_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  /* tslint:disable-next-line: no-use-before-declare */
  useExisting: forwardRef(() => TimepickerComponent),
  multi: true
};

const HOURS_PER_DAY_HALF = 12;
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;

@Component({
  selector: 'gcc-timepicker',
  templateUrl: './timepicker.component.html',
  styleUrls: ['./timepicker.component.scss'],
  providers: [TIMEPICKER_CONTROL_VALUE_ACCESSOR, TIMEPICKER_VALIDATOR]
})
export class TimepickerComponent extends WithSubscription()
  implements ControlValueAccessor, Validator, OnInit, AfterViewInit {
  @ViewChild('hoursInput', { read: ElementRef }) public hoursInput: ElementRef<HTMLInputElement>;
  @ViewChild('minutesInput', { read: ElementRef }) public minutesInput: ElementRef<HTMLInputElement>;
  @ViewChild('meridianInput', { read: ElementRef }) public meridianInput: ElementRef<HTMLInputElement>;

  @Input() public hoursStep = 1;
  @Input() public minutesStep = 15;
  @Input() public showMeridian = true;
  @Input() public isDisabled: boolean;
  @Input() public minDate: Date;
  @Input() public maxDate: Date;

  public viewMode: 'h' | 'm' | 'a' = null;
  public isMinutesFocus = false;
  public isMeridianFocus = false;
  public isDropdownOpen = false;
  public hourOptions: string[] = [];
  public minuteOptions: string[] = [];
  public meridianOptions: string[] = ['AM', 'PM'];

  public hours: string = null;
  public minutes: string = null;
  public seconds: string = null;
  public meridian: string = null;

  private value: Date;
  private ngControl: NgControl;
  private _documentClickListener: () => void;

  constructor(private renderer: Renderer2, private inj: Injector) {
    super();
  }

  public ngOnInit(): void {
    let h = this.showMeridian ? 1 : 0;
    while (h * this.hoursStep <= (this.showMeridian ? 12 : HOURS_PER_DAY - 1)) {
      const hours = (h * this.hoursStep).toString();
      this.hourOptions.push(hours);
      h++;
    }

    let m = 0;
    while (m * this.minutesStep < 60) {
      const minutes = (m * this.minutesStep).toString();
      this.minuteOptions.push(padNumber(minutes));
      m++;
    }
  }

  public ngAfterViewInit(): void {
    this.ngControl = this.inj.get(NgControl);

    this.subs.add(
      this.ngControl.control.valueChanges.subscribe(value => {
        if (!isSame(value, this.value, 'date')) {
          this.writeValue(new Date(value));
          this._onChange(value);
        }
      })
    );
  }

  public writeValue(value: string | Date): void {
    this.value = isValidDate(value) ? parseDate(value) : null;
    this.renderTime(value);
  }

  public onDropdownToggle(event: Event) {
    event.stopPropagation();
    const newValue = !this.isDropdownOpen;

    if (newValue) {
      this._documentClickListener = this.renderer.listen('document', 'click', () => {
        this.isDropdownOpen = false;
        this.viewMode = null;
      });
      this.viewMode = this.viewMode || 'h';
    } else {
      if (this.viewMode) {
        return;
      }
      this._documentClickListener();
    }
    this.isDropdownOpen = newValue;
  }

  public onHoursFocus() {
    this.viewMode = 'h';
  }

  public onHoursChange(event: InputEvent) {
    const value = (event.target as HTMLInputElement).value;
    const hours = +value;
    if (
      !value ||
      (!isNaN(hours) && hours >= (this.showMeridian ? 1 : 0) && hours <= (this.showMeridian ? HOURS_PER_DAY_HALF : 23))
    ) {
      this.hours = value;
    } else {
      (event.target as HTMLInputElement).value = this.hours;
    }
    this.updateTime();
  }

  public onHoursSelect(hours: string) {
    this.hours = hours;
    this.updateTime();
    if (!this.minutes) {
      this.minutesInput.nativeElement.focus();
    } else {
      this.viewMode = null;
    }
  }

  public onMinutesFocus() {
    this.viewMode = 'm';
  }

  public onMinutesBlur() {
    setTimeout(() => {
      this.minutes = padNumber(this.minutes);
      this.updateTime();
    }, 300);
  }

  public onMinutesChange(event: InputEvent) {
    const value = (event.target as HTMLInputElement).value;
    const minutes = +value;
    if (!value || (!isNaN(minutes) && minutes >= 0 && minutes < 60)) {
      this.minutes = value;
    } else {
      (event.target as HTMLInputElement).value = this.minutes;
    }
    this.updateTime();
  }

  public onMinutesSelect(minutes: string) {
    this.minutes = minutes;
    this.updateTime();
    if (this.showMeridian && !this.meridian) {
      this.meridianInput.nativeElement.focus();
    } else {
      this.viewMode = null;
    }
  }

  public onMeridianFocus() {
    this.viewMode = 'a';
  }

  public onMeridianSelect(meridian: string) {
    this.meridian = meridian;
    this.updateTime();
    this.viewMode = null;
  }

  public registerOnChange(fn: (_: any) => {}): void {
    this._onChange = fn;
  }
  public registerOnTouched(fn: () => {}): void {
    this._onTouched = fn;
  }

  public validate(c: AbstractControl): ValidationErrors | null {
    const value: Date | string = c.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (!isValidDate(value)) {
      return {
        invalid: true
      };
    }
    const date = parseDate(value);

    if (this.minDate) {
      // const normalizedValue = new Date(
      //   this.minDate.getFullYear(),
      //   this.minDate.getMonth(),
      //   this.minDate.getDate(),
      //   date.getHours(),
      //   date.getMinutes(),
      //   date.getSeconds(),
      //   date.getMilliseconds()
      // );
      return isBefore(date, this.minDate, 'milliseconds') ? { minDate: true } : null;
    }
    if (this.maxDate) {
      // const normalizedValue = new Date(
      //   this.maxDate.getFullYear(),
      //   this.maxDate.getMonth(),
      //   this.maxDate.getDate(),
      //   date.getHours(),
      //   date.getMinutes(),
      //   date.getSeconds(),
      //   date.getMilliseconds()
      // );
      return isAfter(date, this.maxDate, 'milliseconds') ? { maxDate: true } : null;
    }
  }

  private updateTime() {
    this._onTouched();
    if (!isValidTime(this.hours, this.minutes, this.seconds, this.meridian, this.isPM())) {
      return;
    }

    let hours = parseInt(this.hours, 10);
    const minutes = parseInt(this.minutes, 10);
    const seconds = parseInt(this.seconds, 10);

    if (this.isPM() && hours !== 12) {
      hours += HOURS_PER_DAY_HALF;
    }
    if (this.isAM() && hours === 12) {
      hours = 0;
    }
    let date = this.value;
    if (!date) {
      date = new Date();
    }
    this._onChange(new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, seconds, 0));
  }

  private renderTime(value: string | Date) {
    if (!isValidDate(value)) {
      this.hours = '';
      this.minutes = '';
      this.seconds = '00';
      this.meridian = '';

      return;
    }

    const date = parseDate(value);

    let hours = date.getHours();

    if (this.showMeridian) {
      this.meridian = this.meridianOptions[hours >= HOURS_PER_DAY_HALF ? 1 : 0];
      hours = hours % HOURS_PER_DAY_HALF;
      // should be 12 PM, not 00 PM
      if (hours === 0) {
        hours = HOURS_PER_DAY_HALF;
      }
    }
    this.hours = hours.toString();
    this.minutes = padNumber(date.getMinutes());
    this.seconds = padNumber(date.getSeconds());
  }

  private isPM(): boolean {
    return this.showMeridian && this.meridian === this.meridianOptions[1];
  }

  private isAM(): boolean {
    return this.showMeridian && this.meridian === this.meridianOptions[0];
  }

  private _onChange: (value: Date) => void = () => {};
  private _onTouched: () => void = () => {};
}

export function padNumber(value: number | string): string {
  const v = value.toString();
  if (v.length > 1) {
    return v;
  }

  return `0${v}`;
}

export function isValidDate(value: string | Date): boolean {
  if (!value) {
    return false;
  }

  if (value instanceof Date && isNaN(value.getHours())) {
    return false;
  }

  if (typeof value === 'string') {
    return isValidDate(new Date(value));
  }

  return true;
}

export function isValidTime(hours: string, minutes: string, seconds: string, meridian: string, isPM: boolean): boolean {
  const h = parseInt(hours, 10);
  if (isNaN(h) || h < 0 || h > (isPM ? HOURS_PER_DAY_HALF : HOURS_PER_DAY)) {
    return false;
  }
  const m = parseInt(minutes, 10);
  if (isNaN(m) || m < 0 || m > MINUTES_PER_HOUR) {
    return false;
  }
  const s = parseInt(seconds, 10);
  if (isNaN(s) || s < 0 || s > SECONDS_PER_MINUTE) {
    return false;
  }
  if (!meridian) {
    return false;
  }
  return true;
}

export function parseDate(value: string | Date): Date {
  if (typeof value === 'string') {
    return new Date(value);
  }

  return value;
}
